/**
 * Command Palette global (Cmd+K / Ctrl+K).
 *
 * Búsqueda fuzzy sobre:
 *  - Acciones: ir a Catálogo, Mis instancias, Home, tema light/dark/system, logout.
 *  - Context Owners: cambio rápido entre los N COs del usuario.
 *  - Entidades del catálogo (133 entidades cacheadas via useCatalogStore).
 *  - Mis instancias recientes (últimas N del CO activo).
 *
 * Selección con Enter navega/ejecuta + cierra. Esc cierra.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Home,
  FolderOpen,
  Database,
  Users,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Building2,
  Box,
  Boxes,
  Network,
  ShoppingCart,
  Wand2,
  Palette,
  UserPlus,
  Rocket,
  Activity,
  Brain,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth'
import { useCatalogStore } from '@/stores/catalog'
import { useTheme } from './ThemeProvider'
import { api } from '@/lib/api'
import { toastError } from '@/lib/errores'
import { logAarpia } from '@/lib/log'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'

interface InstanciaReciente {
  id: string
  attributes: {
    entidad_nombre: string
    entidad_sub: string
    entidad_token: string
    entidad_estado_deseable: string
    entidad_version: string
    current_state: string
  }
}

interface Props {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate()
  const token = useAuthStore((s) => s.token)
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const selectCo = useAuthStore((s) => s.selectCo)
  const logout = useAuthStore((s) => s.logout)
  const entidades = useCatalogStore((s) => s.entidades)
  const loadCatalog = useCatalogStore((s) => s.load)
  const { setTheme } = useTheme()

  const [instanciasRecientes, setInstanciasRecientes] = useState<InstanciaReciente[]>([])

  useEffect(() => {
    if (open) {
      loadCatalog(token)
      // Carga top 10 instancias recientes lazy
      api
        .get<{ data: InstanciaReciente[] }>(`/mis-instancias?limit=10`, token)
        .then((r) => setInstanciasRecientes(r.data || []))
        .catch((e) => {
          // No es crítico (sección "recientes" del palette); no interrumpir con toast.
          logAarpia.warn('No se pudieron cargar las instancias recientes:', e?.message || e)
        })
    }
  }, [open, token, loadCatalog])

  function go(path: string) {
    navigate(path)
    onClose()
  }

  async function handleSelectCo(coId: string, nombre: string) {
    try {
      await selectCo(coId)
      toast.success(`Operando como ${nombre}`)
      navigate('/app', { replace: true })
    } catch (e: any) {
      toastError(e, 'Switch-CO falló')
    } finally {
      onClose()
    }
  }

  function handleTheme(t: 'light' | 'dark' | 'system') {
    setTheme(t)
    toast.success(`Tema: ${t}`)
    onClose()
  }

  function handleLogout() {
    logout()
    toast.success('Sesión cerrada')
    navigate('/login', { replace: true })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="overflow-hidden p-0 max-w-2xl">
        <DialogTitle className="sr-only">Buscar</DialogTitle>
        <Command shouldFilter={true} className="rounded-lg">
          <CommandInput placeholder="Busca módulos, fichas, organizaciones o acciones..." />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>

            <CommandGroup heading="Ir a">
              <CommandItem onSelect={() => go('/app')} value="inicio home">
                <Home className="h-4 w-4" />
                <span>Inicio</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/catalogo')} value="catalogo entidades biblioteca modulos">
                <FolderOpen className="h-4 w-4" />
                <span>Catálogo de entidades</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/monitorizacion/fichas')} value="mis instancias fichas registros agregado monitorizacion">
                <Database className="h-4 w-4" />
                <span>Fichas (agregado)</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/marketplace')} value="marketplace tienda modulos contratar">
                <ShoppingCart className="h-4 w-4" />
                <span>Tienda de módulos</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/generar-entidad')} value="generar entidad asistente ia claude descripcion natural">
                <Wand2 className="h-4 w-4" />
                <span>Crear módulo con asistente IA</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/gobierno')} value="gobierno permisos roles organizaciones personas">
                <Users className="h-4 w-4" />
                <span>Gobierno y permisos</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/branding')} value="branding apariencia logo color tema cliente">
                <Palette className="h-4 w-4" />
                <span>Apariencia visual</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/nuevo-cliente')} value="nuevo cliente alta asistente end-to-end">
                <UserPlus className="h-4 w-4" />
                <span>Dar de alta cliente</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/deploy-cliente')} value="deploy publicar cliente nginx letsencrypt subdomain dns">
                <Rocket className="h-4 w-4" />
                <span>Publicar cliente</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/observabilidad')} value="observabilidad monitorizacion grafana paneles">
                <Activity className="h-4 w-4" />
                <span>Monitorización</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/memoria-agente')} value="memoria asistente a60 aprendizajes claude">
                <Brain className="h-4 w-4" />
                <span>Memoria del asistente</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/suscripciones')} value="suscripciones contrataciones contratos">
                <Network className="h-4 w-4" />
                <span>Contratos</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/modulos')} value="modulos superficies contratadas app">
                <Boxes className="h-4 w-4" />
                <span>Módulos</span>
              </CommandItem>
              <CommandItem onSelect={() => go('/app/acerca-de')} value="acerca de glosario atajos">
                <Activity className="h-4 w-4" />
                <span>Acerca del Arquitecto</span>
              </CommandItem>
            </CommandGroup>

            {contextOwners.length > 1 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Cambiar de organización">
                  {contextOwners.map((co) => (
                    <CommandItem
                      key={co.id}
                      onSelect={() => handleSelectCo(co.id, co.nombre)}
                      value={`co ${co.nombre} ${co.nivel}`}
                    >
                      <Building2 className="h-4 w-4" />
                      <span>{co.nombre}</span>
                      <span className="ml-auto text-xs text-muted-foreground font-mono">{co.nivel}</span>
                      {co.id === activeCoId && <span className="text-xs text-primary">activo</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {entidades.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={`Módulos del catálogo (${entidades.length})`}>
                  {entidades.slice(0, 80).map((e) => (
                    <CommandItem
                      key={e.nombre}
                      onSelect={() => go(`/app/catalogo/${e.sub}/${e.token}/${e.estado}/v${e.version}`)}
                      value={`entidad ${e.nombre} ${e.sub} ${e.token} ${e.estado}`}
                    >
                      <Box className="h-4 w-4" />
                      <span className="font-mono text-xs truncate">{e.nombre}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground">v{e.version}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {instanciasRecientes.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={`Fichas recientes (${instanciasRecientes.length})`}>
                  {instanciasRecientes.map((inst) => {
                    const a = inst.attributes
                    const link = `/app/instancias/${a.entidad_sub}/${a.entidad_token}/${a.entidad_estado_deseable}/v${a.entidad_version}/${inst.id}`
                    return (
                      <CommandItem
                        key={inst.id}
                        onSelect={() => go(link)}
                        value={`instancia ${a.entidad_nombre} ${inst.id} ${a.current_state}`}
                      >
                        <Users className="h-4 w-4" />
                        <span className="font-mono text-xs truncate">{a.entidad_nombre}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground font-mono">
                          {a.current_state}
                        </span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}

            <CommandSeparator />
            <CommandGroup heading="Apariencia">
              <CommandItem onSelect={() => handleTheme('light')} value="tema claro light">
                <Sun className="h-4 w-4" />
                <span>Tema claro</span>
              </CommandItem>
              <CommandItem onSelect={() => handleTheme('dark')} value="tema oscuro dark">
                <Moon className="h-4 w-4" />
                <span>Tema oscuro</span>
              </CommandItem>
              <CommandItem onSelect={() => handleTheme('system')} value="tema sistema automatico">
                <Monitor className="h-4 w-4" />
                <span>Seguir al sistema</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />
            <CommandGroup heading="Sesión">
              <CommandItem onSelect={handleLogout} value="logout salir cerrar sesion">
                <LogOut className="h-4 w-4 text-destructive" />
                <span className="text-destructive">Cerrar sesión</span>
                <CommandShortcut>⇧⌘Q</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
